import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { UploadSimple, Trash, Sparkle, Images } from "@phosphor-icons/react";
import api, { fileUrl } from "../lib/api";
import { useApp } from "../context/AppContext";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Explainer, WarmEmptyState } from "../components/friendly";

const RIGHTS = { owned: "Owned", ugc: "UGC (rights cleared)", ai_generated: "AI generated", licensed: "Licensed" };

export default function Assets() {
  const { activeClientId, activeClient } = useApp();
  const [assets, setAssets] = useState([]);
  const [rights, setRights] = useState("owned");
  const fileRef = useRef();

  const load = useCallback(() => {
    if (activeClientId) api.get(`/assets?client_id=${activeClientId}`).then((r) => setAssets(r.data));
  }, [activeClientId]);
  useEffect(load, [load]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("client_id", activeClientId);
    fd.append("rights", rights);
    fd.append("file", file);
    try {
      await api.post("/assets/upload", fd);
      toast.success("Asset uploaded");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    e.target.value = "";
  };

  const remove = async (id) => { await api.delete(`/assets/${id}`); load(); };

  return (
    <div className="space-y-6 fade-up" data-testid="assets-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display font-bold text-3xl tracking-tight">Asset Library</h1>
          <p className="text-sm text-slate-500 mt-1">Uploads and AI-generated visuals for <b>{activeClient?.name}</b>, with rights tracking.</p>
        </div>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="asset-file-input" />
        <div className="flex gap-2 items-center">
          <Select value={rights} onValueChange={setRights}>
            <SelectTrigger data-testid="asset-rights-select" className="w-[170px] h-9 text-[13px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(RIGHTS).filter(([k]) => k !== "ai_generated").map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button data-testid="asset-upload-button" onClick={() => fileRef.current?.click()} className="rounded-lg gap-2 bg-slate-900 hover:bg-slate-700"><UploadSimple size={15} /> Upload</Button>
        </div>
      </div>
      <Explainer id="assets">The shared media library for this brand. Upload photos and finished designs — AI images from the Composer are saved here automatically. Each asset tracks usage rights, so no legal surprises.</Explainer>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {assets.map((a) => (
          <div key={a.asset_id} data-testid={`asset-card-${a.asset_id}`} className="group bg-white border border-slate-200/80 rounded-xl overflow-hidden card-float hover-lift">
            <div className="aspect-square bg-slate-50 relative">
              {a.type === "video" ? (
                <video src={fileUrl(a.url)} controls className="w-full h-full object-cover" />
              ) : (
                <img src={fileUrl(a.url)} alt={a.name} className="w-full h-full object-cover" />
              )}
              {a.type === "ai_image" && <span className="absolute top-2 left-2 flex items-center gap-1 bg-white/90 rounded-full px-2 py-0.5 text-[10px] font-semibold text-[hsl(9,55%,48%)]"><Sparkle size={10} weight="fill" /> AI</span>}
              {a.mock && <span className="absolute bottom-2 left-2 bg-amber-500 text-white text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full">Mock</span>}
              <button data-testid={`asset-delete-${a.asset_id}`} onClick={() => remove(a.asset_id)}
                className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/90 flex items-center justify-center text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"><Trash size={13} /></button>
            </div>
            <div className="p-3">
              <p className="text-[12px] font-medium truncate">{a.name}</p>
              <p className="text-[10px] uppercase tracking-widest text-slate-400 mt-0.5">{RIGHTS[a.rights] || a.rights}</p>
            </div>
          </div>
        ))}
      </div>
      {!assets.length && <WarmEmptyState icon={Images} title="No assets yet" what="This library fills up two ways: upload your own photos and designs here, or generate images in the Composer — they're saved here automatically." />}
    </div>
  );
}
